import { useQuery } from '@apollo/client/react'
import { Text } from '@chakra-ui/react'
import { PaginationState, SortingState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { AdminPageLayout } from '../../../components/AdminPageLayout/AdminPageLayout'
import { DataTable, DataTablePagination } from '../../../components/DataTable'
import {
  AdminUser,
  adminUsersQuery,
  mapAdminUsersSortingToOrderBy,
} from '../../../services/adminUsersService'
import {
  ConnectionResult,
  getConnectionPageCount,
} from '../../../services/listQuery'
import { UsersAdminFilters, useUsersAdminColumns } from './UsersAdminPage.parts'

export const UsersAdminPage = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'createdDate', desc: true },
  ])
  const [filters, setFilters] = useState({
    email: '',
    name: '',
    nickname: '',
    organization: '',
  })

  const filterInput = useMemo(
    () => ({
      ...(filters.email && { email: { contains: filters.email } }),
      ...(filters.name && { name: { contains: filters.name } }),
      ...(filters.nickname && { nickname: { contains: filters.nickname } }),
      ...(filters.organization && {
        organizations: {
          some: {
            name: { contains: filters.organization },
          },
        },
      }),
    }),
    [filters],
  )

  const { data, previousData, loading } = useQuery(adminUsersQuery, {
    variables: {
      first: pagination.pageSize,
      skip: pagination.pageIndex * pagination.pageSize,
      filter: Object.keys(filterInput).length > 0 ? filterInput : undefined,
      orderBy: mapAdminUsersSortingToOrderBy(sorting),
    },
    notifyOnNetworkStatusChange: true,
  })

  const connection =
    (data as { AdminUser_list?: ConnectionResult<AdminUser> } | undefined)
      ?.AdminUser_list ??
    (
      previousData as
        | { AdminUser_list?: ConnectionResult<AdminUser> }
        | undefined
    )?.AdminUser_list ??
    undefined
  const users = connection?.items ?? []
  const totalCount = connection?.totalCount ?? 0
  const isLoading = loading && connection === undefined
  const pageCount = getConnectionPageCount(totalCount, pagination.pageSize)

  const columns = useUsersAdminColumns()

  return (
    <AdminPageLayout pageTitle="Users">
      <Text mb={4} color="presets.neutral.70">
        List of all users in the system.
      </Text>

      <UsersAdminFilters
        filters={filters}
        setFilters={setFilters}
        setPaginationPageIndex={pageIndex =>
          setPagination(current => ({ ...current, pageIndex }))
        }
      />

      <DataTable
        data={users}
        columns={columns}
        isLoading={isLoading}
        manualPagination
        manualSorting
        manualFiltering
        pagination={pagination}
        pageCount={pageCount}
        onPaginationChange={setPagination}
        sorting={sorting}
        onSortingChange={nextSorting => {
          setSorting(nextSorting)
          setPagination(current => ({ ...current, pageIndex: 0 }))
        }}
        height="calc(100vh - 200px)"
      />
      <DataTablePagination
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        pageCount={pageCount}
        totalCount={totalCount}
        isLoading={isLoading}
        onPageChange={pageIndex =>
          setPagination(current => ({ ...current, pageIndex }))
        }
        onPageSizeChange={pageSize =>
          setPagination({
            pageIndex: 0,
            pageSize,
          })
        }
      />
    </AdminPageLayout>
  )
}
